/**
 * Pure render-decision for a chat file reference (workspace-file-tags; analysis §8).
 * Split out of `<FileChip>` so the resolution + fallback logic is unit-testable
 * without a DOM/query harness.
 *
 * Resolution (CLOSED-WORLD; the tooltip NEVER shows the OS name/path, rule 6):
 *   - source "tag" → resolve the `@tag` body via the case-scope tag index:
 *       · registered → chip labeled `@tag` (tag-first, A8); tooltip = the friendly
 *         title; click opens the file.
 *       · unknown    → literal `@text` (graceful degradation — a broken tag never
 *         errors, it just reads as what the agent wrote).
 *   - source "bare" → the machine-name safety net; resolve the path → a friendly
 *     chip, or render the payload VERBATIM on a miss (non-destructive).
 */

import type { WorkspaceAliasMap, WorkspaceAliasEntry } from "@/hooks/useWorkspaceAliasMap";
import { displayLabelForPath } from "@/lib/workspace/derive-display-name";

export type ChipRender =
  | { kind: "chip"; label: string; tooltip: string; path: string; hasAlias: boolean }
  | { kind: "text"; text: string };

/** The friendly name for a resolved entry (title → alias), never the OS name. */
function friendlyOf(entry: WorkspaceAliasEntry): string {
  return entry.title?.trim() || entry.alias?.trim() || "";
}

export function resolveChipRender(
  payload: string,
  source: "tag" | "bare",
  map: WorkspaceAliasMap,
): ChipRender {
  if (source === "tag") {
    const entry = map.resolveTag(payload);
    if (entry) {
      // Chip shows the tag WITHOUT `@` (tag-first, chip chrome IS the cue); tooltip = the friendly title.
      const friendly = friendlyOf(entry);
      return { kind: "chip", label: entry.tag ?? payload, tooltip: friendly, path: entry.path, hasAlias: !!friendly };
    }
    // Unknown tag → the literal `@text` VERBATIM (the agent's prose is preserved; the
    // surviving `@` signals "this reference didn't resolve" — closed-world miss, §8).
    return { kind: "text", text: `@${payload}` };
  }

  // BARE machine-name safety net.
  const entry = map.resolve(payload);
  if (entry) {
    const friendly = friendlyOf(entry);
    if (friendly) return { kind: "chip", label: friendly, tooltip: friendly, path: entry.path, hasAlias: true };
    // ⚖ S1 — this used to label the chip with the BASENAME while suppressing the
    // tooltip "because it would leak the OS name": it withheld the leak in the
    // tooltip and printed it in the label. Now the label DERIVES a friendly name
    // (agent-plane rule 6), so there is no OS name to leak in either slot. The
    // tooltip stays empty: a derived label needs no gloss, and repeating it would
    // just be noise. `hasAlias` stays FALSE — a derived label is not an alias,
    // and callers keying on it must keep seeing the difference.
    return {
      kind: "chip",
      label: displayLabelForPath(entry.path, entry.name),
      tooltip: "",
      path: entry.path,
      hasAlias: false,
    };
  }
  // Bare miss → render the payload verbatim.
  return { kind: "text", text: payload };
}
